import {
  createFramePayloadSchema,
  frameVariantConfigSchema,
  offersConfigSchema,
  productGridConfigSchema,
  productGridPickSchema,
  reviewConfigSchema,
  updateFramePayloadSchema,
  type CreateFramePayloadParsed,
  type CreateFrameVariantPayloadParsed,
  type FrameVariantConfigParsed,
  type OffersConfigParsed,
  type ProductGridConfigParsed,
  type UpdateFramePayloadParsed,
  type UpdateFrameVariantParsed,
} from "./schemas";
import type {
  ChannelMedium,
  Frame,
  FrameTags,
  FrameVariant,
  FrameVariantConfig,
} from "@/types/dragon-tiles";
import type { z } from "zod";

import { buildFrameMetadataWithSelectedEvent } from "@/features/Connectors/frameEventMetadata";
import { toBackendProductGridItems } from "@/features/EmailEditor/plugins/productGridConstants";

type FrameVariantConfigInput = z.input<typeof frameVariantConfigSchema>;
type FrameVariantContentJson = FrameVariant["content_json"];

// ---------------------------------------------------------------------------
// Config slice builders
// ---------------------------------------------------------------------------

/** Build a validated offers map for a single slug entry. */
export function buildOfferConfigSlice(
  slug: string,
  fallbackCode: string,
): OffersConfigParsed {
  return offersConfigSchema.parse({
    [slug]: { fallback_code: fallbackCode },
  });
}

/** Build a validated product_grids map for a single keyed entry. */
export function buildProductGridConfigSlice(args: {
  key: string;
  items: string;
  maxProducts: number;
  reviews?: z.input<typeof reviewConfigSchema>;
  showSourceBadge?: boolean;
  staticProducts?: z.input<typeof productGridPickSchema>[];
  existing?: ProductGridConfigParsed;
}): Record<string, ProductGridConfigParsed> {
  const entry = productGridConfigSchema.parse({
    ...(args.existing ?? {}),
    items: toBackendProductGridItems(args.items),
    max_products: args.maxProducts,
    ...(args.reviews ? { reviews: args.reviews } : {}),
    ...(args.showSourceBadge ? { show_source_badge: true } : {}),
    ...(args.staticProducts && args.staticProducts.length > 0
      ? { static_products: args.staticProducts }
      : {}),
  });
  return { [args.key]: entry };
}

/** Build subject_line / preview_text fields for frame variant config. */
export function buildSubjectPreviewConfigSlice(args: {
  subjectLine?: string;
  previewText?: string;
}): Pick<FrameVariantConfigParsed, "subject_line" | "preview_text"> {
  const result: Pick<
    FrameVariantConfigParsed,
    "subject_line" | "preview_text"
  > = {};
  if (args.subjectLine !== undefined) {
    result.subject_line = args.subjectLine;
  }
  if (args.previewText !== undefined) {
    result.preview_text = args.previewText;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Config merger
// ---------------------------------------------------------------------------

/**
 * Merge and validate a frame variant config. Use standalone when you need a
 * validated config object outside of a save payload (e.g. optimistic local
 * state, TextComposer extension merging).
 */
export function buildFrameVariantConfig(args: {
  existing?: FrameVariantConfigInput;
  subjectLine?: string;
  previewText?: string;
  productGrids?: FrameVariantConfigInput["product_grids"];
  offers?: FrameVariantConfigInput["offers"];
}): FrameVariantConfigParsed {
  return frameVariantConfigSchema.parse({
    ...(args.existing ?? {}),
    ...(args.subjectLine !== undefined
      ? { subject_line: args.subjectLine }
      : {}),
    ...(args.previewText !== undefined
      ? { preview_text: args.previewText }
      : {}),
    ...(args.productGrids ? { product_grids: args.productGrids } : {}),
    ...(args.offers ? { offers: args.offers } : {}),
  });
}

// ---------------------------------------------------------------------------
// Variant narrowing
// ---------------------------------------------------------------------------

/**
 * Narrow an API `FrameVariant` into the discriminated union accepted by the
 * update schema. Required because `FrameVariant.medium` is the wider
 * `ChannelMedium` union and the schema expects per-medium literal arms.
 *
 * Only call with a persisted variant. New (unsaved) rows belong on the wire
 * as `CreateFrameVariantPayloadParsed` and should bypass narrowing — see
 * `updateFrameVariant`.
 */
export function narrowVariantToUpdate(
  variant: FrameVariant,
): UpdateFrameVariantParsed {
  switch (variant.medium) {
    case "email":
      return { ...variant, medium: "email" };
    case "sms":
      return { ...variant, medium: "sms" };
    case "rcs":
      return { ...variant, medium: "rcs" };
    case "mms":
      return { ...variant, medium: "mms" };
    default:
      return assertNever(variant.medium);
  }
}

// ---------------------------------------------------------------------------
// Metadata
// ---------------------------------------------------------------------------

export function createFrameMetadata(args: {
  selectedEventId?: string;
  existingMetadata?: FrameTags;
}): FrameTags {
  const base = args.existingMetadata ?? {};
  if (!args.selectedEventId) {
    return base;
  }
  return buildFrameMetadataWithSelectedEvent(base, args.selectedEventId);
}

// ---------------------------------------------------------------------------
// Low-level payload validators
// ---------------------------------------------------------------------------

export function createFramePayload(args: {
  siteId: string;
  frameName: string;
  innerTileId: string;
  variants: z.input<typeof createFramePayloadSchema>["frame_variants"];
  metadata?: Record<string, unknown>;
}): CreateFramePayloadParsed {
  return createFramePayloadSchema.parse({
    name: args.frameName,
    is_active: false,
    site: args.siteId,
    inner_tile: args.innerTileId,
    frame_variants: args.variants,
    metadata: args.metadata ?? {},
  });
}

export function updateFramePayload(
  fields: z.input<typeof updateFramePayloadSchema>,
): UpdateFramePayloadParsed {
  return updateFramePayloadSchema.parse(fields);
}

/**
 * Frame-identity fields a variant-carrying save must include. Any payload with
 * `frame_variants` is sent as a PUT (ReplaceFrame), which writes a hardcoded
 * column list unconditionally — an omitted field is written NULL. So we echo the
 * frame's current identity (incl. `metadata`: tags, selected event,
 * transactional flag) to avoid wiping it (FOG-2267). Overridable by callers.
 */
export function frameReplaceFields(frame: Frame) {
  return {
    name: frame.name,
    approval_status: frame.approval_status,
    is_active: frame.is_active,
    slug: frame.slug,
    // Tiles that live outside a dragon tile have no inner tile. An empty string
    // fails payload validation, and there is nothing to preserve, so omit it.
    ...(frame.inner_tile ? { inner_tile: frame.inner_tile } : {}),
    metadata: frame.metadata,
  };
}

// ---------------------------------------------------------------------------
// High-level variant updaters
// ---------------------------------------------------------------------------

interface VariantConfigFields {
  subject_line?: string;
  preview_text?: string;
  product_grids?: FrameVariantConfigInput["product_grids"];
  offers?: FrameVariantConfigInput["offers"];
}

interface UpdateFrameVariantArgs {
  frame: Frame;
  medium: FrameVariant["medium"];
  config?: VariantConfigFields;
  contentText?: string;
  contentJson?: FrameVariantContentJson;
  contentPlaintext?: string;
  editorType?: FrameVariant["editor_type"];
  /** When true, creates a new variant if one with the given medium doesn't exist. */
  createIfMissing?: boolean;
  /** Additional frame-level fields to include in the payload (e.g. name). */
  frameFields?: Omit<
    z.input<typeof updateFramePayloadSchema>,
    "frame_variants"
  >;
}

/**
 * Build an update-frame payload that patches a single variant by medium.
 * Clones the full variant array, applies changes to the target, and validates
 * through Zod. Persisted rows are narrowed to the update-schema shape; rows
 * created via `createIfMissing` go on the wire as create-shaped (no server
 * fields), matching how Text Composer appends new mediums on PATCH.
 */
export function updateFrameVariant(
  args: UpdateFrameVariantArgs,
): UpdateFramePayloadParsed {
  const { frame, medium, createIfMissing, frameFields } = args;

  const existingIndex = frame.frame_variants.findIndex(
    (v) => v.medium === medium,
  );

  let updatedVariants: WireVariantRow[];

  if (existingIndex !== -1) {
    updatedVariants = [...frame.frame_variants];
    const existing = frame.frame_variants[existingIndex];
    updatedVariants[existingIndex] = applyVariantChanges(existing, args);
  } else if (createIfMissing) {
    updatedVariants = [...frame.frame_variants, buildNewVariant(medium, args)];
  } else {
    return updateFramePayload({
      ...frameReplaceFields(frame),
      ...frameFields,
      frame_variants: frame.frame_variants.map(narrowVariantToUpdate),
    });
  }

  return updateFramePayload({
    ...frameReplaceFields(frame),
    ...frameFields,
    frame_variants: updatedVariants.map(toWireVariant),
  });
}

interface UpdateFrameVariantByIdArgs {
  frame: Frame;
  variantId: string;
  contentText?: string;
  contentJson?: FrameVariantContentJson;
  config?: VariantConfigFields;
  /** Additional frame-level fields to include in the payload. */
  frameFields?: Omit<
    z.input<typeof updateFramePayloadSchema>,
    "frame_variants"
  >;
}

/**
 * Build an update-frame payload that patches a single variant by ID.
 * Useful when targeting a specific variant instance (e.g. the selected
 * variant in the frame editor).
 */
export function updateFrameVariantById(
  args: UpdateFrameVariantByIdArgs,
): UpdateFramePayloadParsed {
  const { frame, variantId, frameFields } = args;

  const updatedVariants = frame.frame_variants.map((v) =>
    v.id === variantId ? applyVariantChanges(v, args) : v,
  );

  return updateFramePayload({
    ...frameReplaceFields(frame),
    ...frameFields,
    frame_variants: updatedVariants.map(narrowVariantToUpdate),
  });
}

// ---------------------------------------------------------------------------
// Frame inspection (read helpers)
// ---------------------------------------------------------------------------

/** Returns true when config contains at least one product grid entry. */
export function configHasProductGrids(
  config: FrameVariantConfig | undefined | null,
): boolean {
  return Boolean(
    config?.product_grids && Object.keys(config.product_grids).length > 0,
  );
}

/**
 * Renderable email body for HTML display surfaces (preview drawer, broadcast
 * preview iframes). True-plaintext variants keep their body in
 * `content_plaintext` (`content_text` stays empty), so those surfaces get it
 * wrapped in an escaped `<pre>`; everything else passes `content_text`
 * through unchanged. Requests to the renderer (live preview, test sends)
 * submit the raw body via `getEmailVariantPlaintextBody` instead.
 */
export function getEmailVariantPreviewHtml(
  variant:
    | Pick<FrameVariant, "content_text" | "content_plaintext">
    | null
    | undefined,
): string {
  const plaintext = variant?.content_plaintext;
  if (plaintext && !variant?.content_text) {
    return plaintextToDisplayHtml(plaintext);
  }
  return variant?.content_text ?? "";
}

/**
 * Request fields + display selector for rendering an email variant through the
 * frame-variant preview endpoint. Plaintext variants submit `content_plaintext`
 * and display the rendered `plaintext_body` (wrapped for iframe surfaces);
 * HTML variants submit `content_text` and display `html_body` unchanged.
 */
export function emailVariantPreviewRequest(
  variant:
    | Pick<FrameVariant, "content_text" | "content_plaintext">
    | null
    | undefined,
) {
  const contentPlaintext = getEmailVariantPlaintextBody(variant);
  const contentText =
    contentPlaintext !== undefined ? "" : (variant?.content_text ?? "");
  return {
    contentText,
    contentPlaintext,
    hasContent: !!contentText || contentPlaintext !== undefined,
    selectDisplayHtml: (res: {
      email_preview: { html_body: string; plaintext_body?: string };
    }): string =>
      contentPlaintext !== undefined
        ? plaintextToDisplayHtml(res.email_preview.plaintext_body ?? "")
        : res.email_preview.html_body,
  };
}

/** Escape + `<pre>`-wrap plain text so an HTML surface (iframe) shows it faithfully. */
export function plaintextToDisplayHtml(text: string): string {
  const escaped = text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
  // system-ui ≈ what Gmail/Apple Mail/Outlook.com show for text/plain; a bare
  // iframe would otherwise fall back to the browser's serif default.
  return `<pre style="white-space:pre-wrap;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;margin:0">${escaped}</pre>`;
}

/**
 * The raw plain-text body when the variant is a true-plaintext email (no HTML
 * body). Test sends and previews submit this instead of `content_text` so the
 * backend does a real text/plain send (ENG-1415).
 */
export function getEmailVariantPlaintextBody(
  variant:
    | Pick<FrameVariant, "content_text" | "content_plaintext">
    | null
    | undefined,
): string | undefined {
  // Nullish, not truthy: an empty plaintext body still selects the
  // text/plain path (backend rejects a truly bodiless send loudly).
  return !variant?.content_text && variant?.content_plaintext != null
    ? variant.content_plaintext
    : undefined;
}

export function getFrameVariant(
  frame: Frame | null | undefined,
  medium: ChannelMedium,
): FrameVariant | undefined {
  return frame?.frame_variants.find((variant) => variant.medium === medium);
}

/** Whether a frame variant for the given medium has product grids in config. */
export function frameHasProductGrids(
  frame: Frame | null | undefined,
  medium: ChannelMedium = "email",
): boolean {
  return configHasProductGrids(getFrameVariant(frame, medium)?.config);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function assertNever(value: never): never {
  void value;
  throw new Error("Unsupported frame variant medium");
}

function applyVariantChanges(
  variant: FrameVariant,
  changes: {
    config?: VariantConfigFields;
    contentText?: string;
    contentJson?: FrameVariantContentJson;
    contentPlaintext?: string;
    editorType?: FrameVariant["editor_type"];
  },
): FrameVariant {
  const nextConfig =
    changes.config !== undefined
      ? buildFrameVariantConfig({
          existing: variant.config,
          subjectLine: changes.config.subject_line,
          previewText: changes.config.preview_text,
          productGrids: changes.config.product_grids,
          offers: changes.config.offers,
        })
      : variant.config;

  return {
    ...variant,
    ...(changes.contentText !== undefined
      ? { content_text: changes.contentText }
      : {}),
    ...(changes.contentJson !== undefined
      ? { content_json: changes.contentJson }
      : {}),
    ...(changes.contentPlaintext !== undefined
      ? { content_plaintext: changes.contentPlaintext }
      : {}),
    ...(changes.editorType !== undefined
      ? { editor_type: changes.editorType }
      : {}),
    config: nextConfig,
  };
}

/**
 * A row in the working `frame_variants` array of an update-frame builder:
 * either a persisted `FrameVariant` from the cache, or a brand-new
 * `CreateFrameVariantPayloadParsed` row produced by `buildNewVariant`. The
 * union mirrors the merged-rows pattern in
 * `buildUpdateTextComposerFramePayload`.
 */
type WireVariantRow = FrameVariant | CreateFrameVariantPayloadParsed;

function isPersistedVariant(row: WireVariantRow): row is FrameVariant {
  return "id" in row && row.id.length > 0;
}

function toWireVariant(
  row: WireVariantRow,
): UpdateFrameVariantParsed | CreateFrameVariantPayloadParsed {
  return isPersistedVariant(row) ? narrowVariantToUpdate(row) : row;
}

function buildNewVariant(
  medium: FrameVariant["medium"],
  args: {
    config?: VariantConfigFields;
    contentText?: string;
    contentJson?: FrameVariantContentJson;
    contentPlaintext?: string;
    editorType?: FrameVariant["editor_type"];
  },
): CreateFrameVariantPayloadParsed {
  const config = args.config
    ? buildFrameVariantConfig({
        subjectLine: args.config.subject_line,
        previewText: args.config.preview_text,
        productGrids: args.config.product_grids,
        offers: args.config.offers,
      })
    : {};

  const base = {
    medium,
    editor_type: args.editorType ?? "grapesjs_email",
    content_text: args.contentText ?? "",
    content_url: "",
    content_json: args.contentJson ?? null,
    ...(args.contentPlaintext !== undefined
      ? { content_plaintext: args.contentPlaintext }
      : {}),
    config,
  };

  switch (medium) {
    case "email":
      return { ...base, medium: "email" };
    case "sms":
      return { ...base, medium: "sms" };
    case "mms":
      return { ...base, medium: "mms" };
    case "rcs":
      return { ...base, medium: "rcs" };
    default:
      return assertNever(medium);
  }
}
